<template>
  <main
    class="device-config-page px"
    @transitionend.self="refreshMicrophoneAnimation"
  >
    <CyberboardR6CdcPanel
      :baseData="profileNew.data"
      :defaultData="profileNew.default"
      :syncStatus="syncStatus"
      @ready="onCdcReady"
      @state="onCdcState"
      @info="onCdcInfo"
      @error="onCdcError"
    />
    <CyberboardR6Navigation :deviceInfo="deviceInfo" />

    <div v-if="deviceInfo && !audioSupported" class="unsupported-device">
      {{ $t("deviceConfig.unsupportedDevice") }}
    </div>

    <section
      v-show="audioSupported"
      class="audio-visual"
      aria-label="声音设备状态"
    >
      <div
        class="speaker-visual speaker-visual-left"
        :class="{ active: settings.speakerMask & 1 }"
      >
        <a-icon type="sound" class="speaker-icon" aria-hidden="true" />
        <div class="visual-caption">{{ $t("deviceConfig.visualLeft") }}</div>
      </div>

      <div class="microphone-visual">
        <button
          class="microphone-animation"
          :class="{ active: state === 'connected', recording }"
          type="button"
          :disabled="recordingStarting || calibrationRunning"
          :aria-label="
            recording
              ? $t('deviceConfig.stopRecording')
              : $t('deviceConfig.startRecording')
          "
          @click="toggleRecording"
        >
          <span
            ref="microphoneAnimation"
            class="microphone-animation-stage"
          ></span>
        </button>
        <div class="visual-caption microphone-caption">
          <span>{{ $t("deviceConfig.visualMicrophone") }}</span>
          <a-tooltip
            placement="top"
            overlayClassName="device-audio-tooltip px"
            :trigger="['hover', 'focus', 'click']"
          >
            <template slot="title">
              {{ $t("deviceConfig.audioTestHint") }}
            </template>
            <button
              class="visual-help-icon"
              type="button"
              :aria-label="$t('deviceConfig.audioTestHint')"
            >
              <a-icon type="question-circle" />
            </button>
          </a-tooltip>
        </div>
      </div>

      <div
        class="speaker-visual speaker-visual-right"
        :class="{ active: settings.speakerMask & 2 }"
      >
        <a-icon type="sound" class="speaker-icon" aria-hidden="true" />
        <div class="visual-caption">{{ $t("deviceConfig.visualRight") }}</div>
      </div>
    </section>

    <section v-show="audioSupported" class="recording-panel">
      <div v-if="recording || recordingUrl" class="recording-content">
        <audio
          v-if="!recording"
          ref="recordingPlayer"
          :src="recordingUrl"
          @loadedmetadata="syncPlayback"
          @durationchange="syncPlayback"
          @timeupdate="syncPlayback"
          @play="syncPlayback"
          @pause="syncPlayback"
          @ended="syncPlayback"
        />
        <div class="recording-visual">
          <div
            v-if="recording"
            ref="recordingAnimation"
            class="recording-animation"
          ></div>
          <button
            v-else
            class="recording-play"
            type="button"
            :disabled="!recordingUrl || calibrationRunning"
            :aria-label="
              isPlaying
                ? $t('deviceConfig.pauseRecording')
                : $t('deviceConfig.playRecording')
            "
            @click="togglePlayback"
          >
            <a-icon :type="isPlaying ? 'pause' : 'caret-right'" />
          </button>
        </div>
        <div class="recording-copy">
          <div class="recording-title">
            {{
              recording
                ? $t("deviceConfig.recordingInProgress")
                : $t("deviceConfig.recordingClip")
            }}
          </div>
          <div class="recording-meta">
            {{ recording ? recordingTimeLabel : playbackTimeLabel }}
          </div>
          <input
            class="recording-seek"
            :style="{ '--recording-progress': `${recordingProgress}%` }"
            type="range"
            min="0"
            :max="recording ? 60 : recordingDuration"
            step="any"
            :value="recording ? recordingElapsed : playbackTime"
            :disabled="
              recording || calibrationRunning || recordingDuration <= 0
            "
            :aria-label="$t('deviceConfig.playbackProgress')"
            @input="previewPlayback"
            @change="seekPlayback"
            @pointercancel="seekPlayback"
            @blur="seeking && seekPlayback($event)"
          />
        </div>
        <div class="recording-actions">
          <button
            v-if="recording"
            class="recording-stop"
            type="button"
            @click="stopRecording"
          >
            <span class="recording-stop-mark" aria-hidden="true"></span>
            {{ $t("deviceConfig.stopRecording") }}
          </button>
        </div>
      </div>
    </section>

    <section v-show="audioSupported" class="device-config-grid">
      <article class="audio-panel speaker-panel">
        <div class="setting-block">
          <div class="setting-label">
            {{ $t("deviceConfig.speakerOutput") }}
          </div>
          <a-radio-group
            class="device-radio-group"
            size="large"
            :value="settings.speakerMask"
            :disabled="busy || state !== 'connected'"
            @change="onSpeakerChange"
          >
            <a-radio-button
              v-for="option in speakerOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </a-radio-button>
          </a-radio-group>
        </div>

        <div class="setting-block calibration-block">
          <div class="setting-label calibration-label">
            <span>{{ $t("deviceConfig.speakerCalibration") }}</span>
            <a-tooltip
              placement="top"
              overlayClassName="device-audio-tooltip px"
              :trigger="['hover', 'focus', 'click']"
            >
              <template slot="title">
                {{ $t("deviceConfig.calibrationHint") }}
              </template>
              <button
                class="visual-help-icon"
                type="button"
                :aria-label="$t('deviceConfig.calibrationHint')"
              >
                <a-icon type="question-circle" />
              </button>
            </a-tooltip>
          </div>
          <button
            class="outline-button"
            type="button"
            :disabled="
              busy || state !== 'connected' || recording || recordingStarting
            "
            @click="calibrateSpeaker"
          >
            <span class="button-led"></span>
            {{
              calibrationRunning
                ? $t("deviceConfig.calibrating")
                : $t("deviceConfig.calibrate")
            }}
          </button>
          <div
            v-if="calibrationText"
            class="calibration-result"
            :class="calibrationResult.status"
            role="status"
            aria-live="polite"
          >
            {{ calibrationText }}
          </div>
        </div>
      </article>

      <article class="audio-panel microphone-panel">
        <div class="setting-block">
          <div class="setting-label">{{ $t("deviceConfig.micDirection") }}</div>
          <a-radio-group
            class="device-radio-group"
            size="large"
            :value="settings.micDirection"
            :disabled="busy || state !== 'connected'"
            @change="onMicrophoneChange('micDirection', $event)"
          >
            <a-radio-button
              v-for="option in directionOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </a-radio-button>
          </a-radio-group>
        </div>

        <div class="setting-block">
          <div class="setting-label">
            {{ $t("deviceConfig.noiseReduction") }}
          </div>
          <a-radio-group
            class="device-radio-group"
            size="large"
            :value="settings.noiseReduction"
            :disabled="busy || state !== 'connected'"
            @change="onMicrophoneChange('noiseReduction', $event)"
          >
            <a-radio-button
              v-for="option in noiseOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </a-radio-button>
          </a-radio-group>
        </div>

        <div class="setting-block">
          <div class="setting-label">
            {{ $t("deviceConfig.micSoundEffect") }}
          </div>
          <a-radio-group
            class="device-radio-group"
            size="large"
            :value="settings.soundEffect"
            :disabled="busy || state !== 'connected'"
            @change="onMicrophoneChange('soundEffect', $event)"
          >
            <a-radio-button
              v-for="option in soundOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </a-radio-button>
          </a-radio-group>
        </div>
      </article>
    </section>
  </main>
</template>

<script>
import lottie from "lottie-web";
import { mapState } from "vuex";
import micAnimation from "@/assets/Mic.json";
import recordingAnimationData from "@/assets/Private.json";
import CyberboardR6CdcPanel from "./CyberboardR6/CyberboardR6CdcPanel.vue";
import CyberboardR6Navigation from "./CyberboardR6/CyberboardR6Navigation.vue";
import { getR6ConnectionErrorMessage } from "./CyberboardR6/utils/cyberboardR6ErrorMessage";
import { frames } from "./CyberboardR6/utils/cyberboardR6CdcProtocol";

const wait = (milliseconds) =>
  new Promise((resolve) => setTimeout(resolve, milliseconds));
const MAX_RECORDING_SECONDS = 60;
const createSilentWav = () => {
  const sampleRate = 48000;
  const dataSize = sampleRate * 2 * 2;
  const buffer = new ArrayBuffer(44 + dataSize);
  const header = new DataView(buffer);
  for (const [offset, text] of [
    [0, "RIFF"],
    [8, "WAVE"],
    [12, "fmt "],
    [36, "data"],
  ]) {
    for (let i = 0; i < text.length; i++)
      header.setUint8(offset + i, text.charCodeAt(i));
  }
  header.setUint32(4, 36 + dataSize, true);
  header.setUint32(16, 16, true);
  header.setUint16(20, 1, true);
  header.setUint16(22, 2, true);
  header.setUint32(24, sampleRate, true);
  header.setUint32(28, sampleRate * 4, true);
  header.setUint16(32, 4, true);
  header.setUint16(34, 16, true);
  header.setUint32(40, dataSize, true);
  return new Blob([buffer], { type: "audio/wav" });
};
const formatTime = (time) => {
  const seconds = Math.max(0, Math.floor(time));
  return `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(
    seconds % 60
  ).padStart(2, "0")}`;
};

export default {
  name: "DeviceConfig",
  components: {
    CyberboardR6CdcPanel,
    CyberboardR6Navigation,
  },
  data() {
    return {
      bridge: null,
      deviceInfo: null,
      microphoneAnimation: null,
      recordingAnimation: null,
      recorder: null,
      recordingStream: null,
      recordingUrl: "",
      recording: false,
      recordingStarting: false,
      recordingStartedAt: 0,
      recordingElapsed: 0,
      recordingDuration: 0,
      playbackTime: 0,
      playbackFrame: null,
      seeking: false,
      recordingTimer: null,
      recordingStopTimer: null,
      isPlaying: false,
      state: "disconnected",
      busy: false,
      syncStatus: "",
      calibrationRunning: false,
      calibrationAudio: null,
      calibrationResult: { status: "", left: 0, right: 0 },
      settings: {
        speakerMask: 3,
        micDirection: 0x15,
        noiseReduction: 1,
        soundEffect: 1,
      },
      speakerOptions: [
        { value: 0, label: this.$t("deviceConfig.speakerOff") },
        { value: 1, label: this.$t("deviceConfig.speakerLeft") },
        { value: 2, label: this.$t("deviceConfig.speakerRight") },
        { value: 3, label: this.$t("deviceConfig.speakerBoth") },
      ],
      directionOptions: [
        { value: 0x15, label: this.$t("deviceConfig.directionForward") },
        { value: 0x20, label: this.$t("deviceConfig.directionBackward") },
        { value: 0x30, label: this.$t("deviceConfig.directionOmni") },
      ],
      noiseOptions: [
        { value: 0, label: this.$t("deviceConfig.noiseMax") },
        { value: 1, label: this.$t("deviceConfig.noiseLight") },
        { value: 2, label: this.$t("deviceConfig.noiseOff") },
      ],
      soundOptions: [
        { value: 1, label: this.$t("deviceConfig.soundNormal") },
        { value: 2, label: this.$t("deviceConfig.soundBright") },
        { value: 3, label: this.$t("deviceConfig.soundFull") },
      ],
    };
  },
  watch: {
    audioSupported(supported) {
      if (supported) {
        this.$nextTick(() => this.initMicrophoneAnimation());
      } else if (this.microphoneAnimation) {
        this.microphoneAnimation.destroy();
        this.microphoneAnimation = null;
      }
    },
    recording(isRecording) {
      this.updateMicrophoneAnimation();
      if (!isRecording) {
        if (this.recordingAnimation) this.recordingAnimation.destroy();
        this.recordingAnimation = null;
        return;
      }
      this.$nextTick(() => {
        if (!this.recording || !this.$refs.recordingAnimation) return;
        this.recordingAnimation = lottie.loadAnimation({
          container: this.$refs.recordingAnimation,
          renderer: "canvas",
          loop: true,
          autoplay: true,
          animationData: recordingAnimationData,
        });
      });
    },
  },
  mounted() {
    if (this.audioSupported) this.initMicrophoneAnimation();
  },
  beforeDestroy() {
    if (this.calibrationAudio) {
      this.calibrationAudio.pause();
      this.calibrationAudio.src = "";
    }
    if (this.microphoneAnimation) {
      this.microphoneAnimation.destroy();
      this.microphoneAnimation = null;
    }
    if (this.recordingAnimation) {
      this.recordingAnimation.destroy();
      this.recordingAnimation = null;
    }
    this.stopPlayback();
    this.stopRecording();
    this.recording = false;
    if (this.recordingUrl) URL.revokeObjectURL(this.recordingUrl);
  },
  computed: {
    ...mapState(["profileNew"]),
    audioSupported() {
      return (
        this.state === "connected" &&
        this.bridge?.isConnected &&
        this.bridge.deviceInfo?.audioSupported === true
      );
    },
    recordingTimeLabel() {
      return `${formatTime(this.recordingElapsed)} / ${formatTime(
        MAX_RECORDING_SECONDS
      )}`;
    },
    playbackTimeLabel() {
      return `${formatTime(this.playbackTime)} / ${formatTime(
        this.recordingDuration
      )}`;
    },
    recordingProgress() {
      const duration = this.recording
        ? MAX_RECORDING_SECONDS
        : this.recordingDuration;
      const time = this.recording ? this.recordingElapsed : this.playbackTime;
      return duration > 0
        ? Math.min(100, Math.max(0, (time / duration) * 100))
        : 0;
    },
    calibrationText() {
      if (["passed", "failed"].includes(this.calibrationResult.status)) {
        return `${this.$t(
          this.calibrationResult.status === "passed"
            ? "deviceConfig.calibrationPassed"
            : "deviceConfig.calibrationFailed"
        )} · ${this.$t("deviceConfig.calibrationImpedance", [
          this.calibrationResult.left.toFixed(2),
          this.calibrationResult.right.toFixed(2),
        ])}`;
      }
      if (this.calibrationResult.status === "error") {
        return this.$t("deviceConfig.calibrationError", [
          this.calibrationResult.error,
        ]);
      }
      return "";
    },
  },
  methods: {
    initMicrophoneAnimation() {
      if (
        !this.audioSupported ||
        this.microphoneAnimation ||
        !this.$refs.microphoneAnimation
      )
        return;
      this.microphoneAnimation = lottie.loadAnimation({
        container: this.$refs.microphoneAnimation,
        renderer: "canvas",
        loop: false,
        autoplay: false,
        animationData: micAnimation,
      });
      this.microphoneAnimation.addEventListener(
        "DOMLoaded",
        this.updateMicrophoneAnimation
      );
    },
    refreshMicrophoneAnimation(event) {
      if (
        event.propertyName !== "opacity" ||
        !this.audioSupported ||
        !this.microphoneAnimation
      )
        return;
      this.microphoneAnimation.resize();
    },
    updateMicrophoneAnimation() {
      if (!this.microphoneAnimation?.isLoaded) return;
      // Mic.json: frames 0–144 turn gray to purple; 144–287 turn purple to gray.
      this.microphoneAnimation.playSegments(
        this.recording ? [144, 287] : [0, 144],
        true
      );
    },
    async toggleRecording() {
      if (
        !this.audioSupported ||
        this.recordingStarting ||
        this.calibrationRunning
      )
        return;
      if (this.recording) {
        this.stopRecording();
        return;
      }
      if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
        this.showError(new Error(this.$t("deviceConfig.recordingUnsupported")));
        return;
      }
      this.recordingStarting = true;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        if (
          this._isBeingDestroyed ||
          this._isDestroyed ||
          !this.audioSupported
        ) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        this.recordingStream = stream;
        const options = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? { mimeType: "audio/webm;codecs=opus" }
          : undefined;
        const recorder = new MediaRecorder(stream, options);
        const chunks = [];
        this.recorder = recorder;
        this.recordingElapsed = 0;
        recorder.ondataavailable = (event) => {
          if (event.data.size) chunks.push(event.data);
        };
        recorder.onstop = () => {
          if (this._isBeingDestroyed || this._isDestroyed) return;
          this.stopRecording();
          const blob = new Blob(chunks, {
            type: recorder.mimeType || "audio/webm",
          });
          if (this.recordingUrl) URL.revokeObjectURL(this.recordingUrl);
          this.recordingUrl = URL.createObjectURL(blob);
          this.recordingDuration = this.recordingElapsed;
          this.playbackTime = 0;
          this.recording = false;
        };
        recorder.start();
        this.stopPlayback();
        this.recording = true;
        this.recordingStartedAt = Date.now();
        this.recordingTimer = window.setInterval(() => {
          this.recordingElapsed = Math.min(
            MAX_RECORDING_SECONDS,
            (Date.now() - this.recordingStartedAt) / 1000
          );
        }, 250);
        this.recordingStopTimer = window.setTimeout(
          () => this.stopRecording(),
          MAX_RECORDING_SECONDS * 1000
        );
      } catch (error) {
        this.stopRecording();
        this.showError(error);
      } finally {
        this.recordingStarting = false;
      }
    },
    stopRecording() {
      if (this.recordingStopTimer) window.clearTimeout(this.recordingStopTimer);
      if (this.recordingTimer) window.clearInterval(this.recordingTimer);
      this.recordingStopTimer = null;
      this.recordingTimer = null;
      if (this.recording) {
        this.recordingElapsed = Math.min(
          MAX_RECORDING_SECONDS,
          (Date.now() - this.recordingStartedAt) / 1000
        );
      }
      if (this.recorder && this.recorder.state !== "inactive")
        this.recorder.stop();
      if (this.recordingStream) {
        this.recordingStream.getTracks().forEach((track) => track.stop());
      }
      this.recordingStream = null;
    },
    async togglePlayback() {
      if (this.calibrationRunning) return;
      const player = this.$refs.recordingPlayer;
      if (!player || !this.recordingUrl) return;
      if (this.isPlaying) {
        player.pause();
        return;
      }
      try {
        if (player.ended) player.currentTime = 0;
        await player.play();
      } catch (error) {
        this.showError(error);
      }
    },
    syncPlayback(event) {
      const player = event.target;
      if (!this.seeking) this.playbackTime = player.currentTime;
      this.isPlaying = !player.paused && !player.ended;
      // MediaRecorder WebM can report Infinity; keep the measured recording duration.
      if (Number.isFinite(player.duration) && player.duration > 0) {
        this.recordingDuration = player.duration;
      }
      if (this.isPlaying && this.playbackFrame === null) {
        this.playbackFrame = window.requestAnimationFrame(
          this.updatePlaybackTime
        );
      } else if (!this.isPlaying && this.playbackFrame !== null) {
        window.cancelAnimationFrame(this.playbackFrame);
        this.playbackFrame = null;
      }
    },
    updatePlaybackTime() {
      this.playbackFrame = null;
      const player = this.$refs.recordingPlayer;
      if (!player || player.paused || player.ended) return;
      if (!this.seeking) this.playbackTime = player.currentTime;
      this.playbackFrame = window.requestAnimationFrame(
        this.updatePlaybackTime
      );
    },
    previewPlayback(event) {
      this.seeking = true;
      this.playbackTime = Number(event.target.value);
    },
    seekPlayback(event) {
      const player = this.$refs.recordingPlayer;
      if (!player) return;
      player.currentTime = Number(event.target.value);
      this.playbackTime = player.currentTime;
      this.seeking = false;
    },
    stopPlayback() {
      if (this.playbackFrame !== null)
        window.cancelAnimationFrame(this.playbackFrame);
      this.playbackFrame = null;
      this.seeking = false;
      const player = this.$refs.recordingPlayer;
      if (player) {
        player.pause();
        player.currentTime = 0;
      }
      this.isPlaying = false;
      this.playbackTime = 0;
    },
    matches(command) {
      return (frame) => frame && frame[0] === 0x07 && frame[1] === command;
    },
    request(frame, command, timeout) {
      if (!this.audioSupported) {
        throw new Error(this.$t("deviceConfig.unsupportedDevice"));
      }
      return this.bridge.cdc.request(frame, this.matches(command), timeout);
    },
    async onCdcReady(bridge) {
      this.bridge = bridge;
      this.state = "connected";
      this.syncStatus = "";
    },
    onCdcInfo(info) {
      this.deviceInfo = info;
    },
    onCdcState(state) {
      this.state = state;
      if (state !== "connected" && this.calibrationAudio)
        this.calibrationAudio.pause();
      if (state !== "connected") {
        this.deviceInfo = null;
        this.stopRecording();
        this.stopPlayback();
        this.syncStatus =
          this.syncStatus === "syncInProgress" ||
          this.syncStatus === "syncUncertain"
            ? "syncUncertain"
            : "";
      }
    },
    onCdcError(error) {
      this.showError(error);
    },
    async setSpeaker(mask) {
      if (!this.audioSupported || this.busy || this.state !== "connected")
        return;
      this.busy = true;
      this.syncStatus = "syncInProgress";
      try {
        // ponytail: protocol has no speaker GET; use the SET echo until firmware adds persistent readback.
        const response = await this.request(frames.speakerSwitch(mask), 0x06);
        if (response[2] !== mask) throw new Error("喇叭开关回显不匹配");
        this.settings.speakerMask = mask;
        this.syncStatus =
          this.state === "connected" ? "syncSynced" : "syncUncertain";
      } catch (error) {
        this.syncStatus =
          this.state === "connected" ? "syncFailed" : "syncUncertain";
        this.showError(error);
      } finally {
        this.busy = false;
      }
    },
    onSpeakerChange(event) {
      this.setSpeaker(Number(event.target.value));
    },
    onMicrophoneChange(key, event) {
      this.setMicrophone(key, Number(event.target.value));
    },
    async setMicrophone(key, mode) {
      if (!this.audioSupported || this.busy || this.state !== "connected")
        return;
      const commands = {
        micDirection: [frames.micDirection(mode), 0x0a],
        noiseReduction: [frames.noiseReduction(mode), 0x08],
        soundEffect: [frames.micSoundEffect(mode), 0x09],
      };
      const [setFrame, setCommand] = commands[key];
      this.busy = true;
      this.syncStatus = "syncInProgress";
      try {
        const response = await this.request(setFrame, setCommand);
        if (response[2] !== mode || response[3] !== 0) {
          throw new Error("麦克风设置未被设备接受");
        }
        this.settings[key] = mode;
        this.syncStatus =
          this.state === "connected" ? "syncSynced" : "syncUncertain";
      } catch (error) {
        this.syncStatus =
          this.state === "connected" ? "syncFailed" : "syncUncertain";
        this.showError(error);
      } finally {
        this.busy = false;
      }
    },
    async calibrateSpeaker() {
      if (
        !this.audioSupported ||
        this.busy ||
        this.state !== "connected" ||
        this.recording ||
        this.recordingStarting
      )
        return;
      this.busy = true;
      this.calibrationRunning = true;
      this.calibrationResult = { status: "", left: 0, right: 0 };
      let wavUrl = "";
      try {
        this.stopPlayback();
        await this.bridge.cdc.enqueue(() => {
          if (!this.audioSupported)
            throw new Error(this.$t("deviceConfig.unsupportedDevice"));
          return this.bridge.cdc.write(frames.speakerTestMode());
        });
        const speakerResponse = await this.request(
          frames.speakerSwitch(3),
          0x06
        );
        if (speakerResponse[2] !== 3)
          throw new Error(this.$t("deviceConfig.calibrationSpeakerError"));
        if (this._isBeingDestroyed || this._isDestroyed) return;
        if (this.state !== "connected")
          throw new Error(this.$t("deviceConfig.calibrationDisconnected"));
        this.settings.speakerMask = 3;
        wavUrl = URL.createObjectURL(createSilentWav());
        const audio = new Audio(wavUrl);
        this.calibrationAudio = audio;
        audio.loop = true;
        audio.onerror = () => audio.pause();
        await audio.play();
        const startedAt = Date.now();
        while (Date.now() - startedAt < 10000) {
          await wait(500);
          if (this._isBeingDestroyed || this._isDestroyed) return;
          if (this.state !== "connected")
            throw new Error(this.$t("deviceConfig.calibrationDisconnected"));
          if (audio.paused || audio.error)
            throw new Error(this.$t("deviceConfig.calibrationPlaybackError"));
        }
        let response;
        const deadline = Date.now() + 15000;
        while (Date.now() < deadline) {
          if (this._isBeingDestroyed || this._isDestroyed) return;
          if (this.state !== "connected")
            throw new Error(this.$t("deviceConfig.calibrationDisconnected"));
          if (audio.paused || audio.error)
            throw new Error(this.$t("deviceConfig.calibrationPlaybackError"));
          response = await this.request(
            frames.speakerCalibrate(),
            0x04,
            Math.min(10000, deadline - Date.now())
          );
          if (response[2] === 1) break;
          if (response[2] !== 0)
            throw new Error(this.$t("deviceConfig.calibrationResponseError"));
          await wait(500);
        }
        if (response?.[2] !== 1)
          throw new Error(this.$t("deviceConfig.calibrationTimeout"));
        if (this._isBeingDestroyed || this._isDestroyed) return;
        if (this.state !== "connected")
          throw new Error(this.$t("deviceConfig.calibrationDisconnected"));
        if (audio.paused || audio.error)
          throw new Error(this.$t("deviceConfig.calibrationPlaybackError"));
        const left = (response[4] | (response[5] << 8)) / 1024;
        const right = (response[6] | (response[7] << 8)) / 1024;
        const passed =
          response[3] === 0 &&
          left >= 5 &&
          left <= 6 &&
          right >= 5 &&
          right <= 6;
        this.calibrationResult = {
          status: passed ? "passed" : "failed",
          left,
          right,
        };
        if (this.$message)
          this.$message[passed ? "success" : "error"](this.calibrationText);
      } catch (error) {
        if (!this._isBeingDestroyed && !this._isDestroyed) {
          this.calibrationResult = { status: "error", error: error.message };
          if (this.$message) this.$message.error(this.calibrationText);
        }
      } finally {
        if (this.calibrationAudio) {
          this.calibrationAudio.pause();
          this.calibrationAudio.src = "";
          this.calibrationAudio = null;
        }
        if (wavUrl) URL.revokeObjectURL(wavUrl);
        this.calibrationRunning = false;
        this.busy = false;
      }
    },
    showError(error) {
      console.error("R6 device operation failed", error?.message || error);
      const connectionMessage = getR6ConnectionErrorMessage(
        error,
        this.$t.bind(this)
      );
      if (connectionMessage) {
        if (this.$message) this.$message.error(connectionMessage);
        return;
      }
      const reason = error?.message || this.$t("deviceConfig.operationFailed");
      if (this.$message) {
        this.$message.error(
          reason
            ? `${this.$t("cyberboardR6.operationFailed")} (${reason})`
            : this.$t("cyberboardR6.operationFailed")
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.device-config-page {
  min-height: inherit;
  width: 100vw;
  padding: 10px 0 30px;
  color: #ffffff;
  text-align: center;
}

.unsupported-device {
  width: min(560px, calc(100vw - 48px));
  margin: 180px auto 0;
  padding: 24px;
  border: 1px solid #454545;
  border-radius: 8px;
  color: #ffffff;
  font-size: 18px;
}

.audio-visual,
.device-config-grid {
  width: 1200px;
  max-width: calc(100vw - 120px);
  margin-right: auto;
  margin-left: auto;
}

.audio-visual {
  width: 960px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: center;
  position: relative;
  height: 240px;
  margin-bottom: 16px;
  border-bottom: 1px solid #1d1d1d;
}

.speaker-visual {
  @include flex-center;
  position: relative;
  flex-direction: column;
  min-width: 0;
  height: 100%;

  &.active .speaker-icon {
    opacity: 1;
  }
}

.speaker-icon {
  @include flex-center;
  width: 174px;
  max-width: 100%;
  height: 174px;
  color: $primary-color;
  font-size: 52px;
  opacity: 0.25;
  transition: opacity 0.2s ease;
}

.microphone-visual {
  @include flex-center;
  position: relative;
  flex-direction: column;
  min-width: 0;
  height: 100%;
}

.microphone-animation {
  position: relative;
  display: block;
  width: 174px;
  max-width: 100%;
  height: 174px;
  overflow: hidden;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &.active {
    opacity: 1;
  }

  &.recording {
    opacity: 1;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 1px solid $primary-color;
    outline-offset: 4px;
  }
}

.microphone-animation-stage {
  position: absolute;
  inset: 0;
  display: block;
  overflow: hidden;
  pointer-events: none;
}

.recording-panel {
  width: 640px;
  max-width: calc(100vw - 120px);
  height: 104px;
  margin: 0 auto 16px;
  padding: 0 12px;
  text-align: left;
}

.recording-content {
  display: grid;
  grid-template-columns: 104px minmax(0, 1fr) 112px;
  gap: 16px;
  align-items: center;
  height: 100%;
}

.recording-visual {
  @include flex-center;
  height: 104px;
}

.recording-animation {
  width: 104px;
  height: 104px;
}

.recording-panel audio {
  display: none;
}

.recording-play {
  @include flex-center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid $primary-color;
  border-radius: 50%;
  background: transparent;
  color: $primary-color;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 4px;
  }

  &:disabled {
    border-color: #444444;
    color: #666666;
    cursor: default;
  }
}

.recording-copy {
  min-width: 0;
}

.recording-stop {
  @include flex-center;
  gap: 8px;
  width: 100%;
  min-height: 36px;
  padding: 6px 8px;
  border: 1px solid #b68aff;
  border-radius: 4px;
  background: transparent;
  color: #e1cfff;
  font-size: 12px;
  cursor: pointer;

  &:hover {
    background: rgba($primary-color, 0.15);
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 4px;
  }
}

.device-config-page.px .recording-stop {
  font-size: max(10px, 0.12rem);
}

.recording-stop-mark {
  flex: 0 0 10px;
  height: 10px;
  border-radius: 1px;
  background: currentColor;
}

.recording-title {
  color: #ffffff;
  font-size: 14px;
}

.recording-meta {
  margin-top: 2px;
  color: #bfbfbf;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}

.device-config-page.px .recording-meta,
.device-config-page.px .panel-index,
.device-config-page.px .panel-heading p {
  font-size: max(10px, 0.12rem);
}

.recording-seek {
  appearance: none;
  -webkit-appearance: none;
  display: block;
  width: 100%;
  height: 24px;
  margin: 6px 0 0;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: 4px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      #a660ff var(--recording-progress),
      #383838 var(--recording-progress)
    );
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    margin-top: -5px;
    border: 0;
    border-radius: 50%;
    background: #a660ff;
    transition: box-shadow 0.15s ease;
  }

  &::-moz-range-track {
    height: 4px;
    border-radius: 4px;
    background: linear-gradient(
      to right,
      #a660ff var(--recording-progress),
      #383838 var(--recording-progress)
    );
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    border: 0;
    border-radius: 50%;
    background: #a660ff;
  }

  &:focus-visible {
    outline: 2px solid $primary-color;
    outline-offset: 4px;
    border-radius: 4px;
  }

  &:hover::-webkit-slider-thumb {
    box-shadow: 0 0 0 4px rgba($primary-color, 0.2);
  }

  &:disabled {
    cursor: default;
  }

  &:disabled::-webkit-slider-thumb {
    visibility: hidden;
  }

  &:disabled::-moz-range-thumb {
    visibility: hidden;
  }
}

.px .visual-caption {
  position: absolute;
  bottom: 24px;
  left: 50%;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  color: #e5e5e5;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: 0;
  transform: translateX(-50%);
}

.px .visual-caption,
.px .calibration-result {
  font-size: max(10px, 0.13rem);
}

.px .visual-help-icon {
  @include flex-center;
  position: absolute;
  left: calc(100% + 8px);
  width: 24px;
  height: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #999999;
  cursor: help;
  font-size: 13px;
}

.outline-button {
  @include button;
  width: 180px;
  margin: 0;
}

.device-config-grid {
  display: grid;
  grid-template-columns: max-content max-content;
  justify-content: center;
  column-gap: 40px;
  row-gap: 20px;
  margin-bottom: 60px;
  text-align: left;
}

.audio-panel {
  min-height: 520px;
  padding: 0 15px 30px;
}

.panel-heading {
  @include flex-center;
  justify-content: flex-start;
  min-height: 50px;
  padding: 0 0 5px;
  border-bottom: 3px solid #1d1d1d;

  h2 {
    font-size: 20px;
  }

  p {
    margin-top: 5px;
    color: #999999;
    font-size: 12px;
  }
}

.panel-index {
  width: 36px;
  color: $primary-color;
  font-size: 12px;
}

.setting-block {
  padding: 20px 0 10px;
}

.setting-label {
  margin-bottom: 10px;
  font-size: 20px;
}

.calibration-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.px .calibration-label .visual-help-icon {
  position: static;
}

.device-radio-group {
  display: flex;
  flex-wrap: wrap;
}

.px .calibration-result {
  margin-top: 12px;
  color: $primary-color;
  max-width: 280px;
  font-size: 13px;
  line-height: 1.5;

  &.passed {
    color: #8bd9a4;
  }

  &.failed,
  &.error {
    color: #ff9898;
  }
}

@media (max-width: 720px) {
  .audio-visual {
    max-width: calc(100vw - 40px);
  }

  .speaker-icon,
  .microphone-animation {
    width: 150px;
    height: 150px;
  }

  .speaker-icon {
    font-size: 44px;
  }

  .recording-panel {
    max-width: calc(100vw - 40px);
  }

  .recording-content {
    grid-template-columns: 80px minmax(0, 1fr) 100px;
    gap: 8px;
  }

  .recording-animation {
    width: 80px;
    height: 80px;
  }

  .device-config-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>

<style lang="scss">
.device-audio-tooltip.px {
  max-width: 300px;

  .ant-tooltip-inner {
    padding: 10px 12px;
    background: #303030;
    color: #f5f5f5;
    font-size: 13px;
    line-height: 1.6;
  }

  .ant-tooltip-arrow::before {
    background: #303030;
  }
}

.microphone-animation-stage canvas {
  display: block;
  width: 100%;
  height: 100%;
}
</style>
